import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  FormHelperText,
  ButtonGroup,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { updateLeadStatus, setLeads, addLead, deleteLead } from '../redux/slices/leadSlice';
import * as mockDataService from '../services/mockData';
import * as api from '../services/api';
import LeadImporter from '../components/LeadImporter';
import LeadTooltip from '../components/LeadTooltip';
import LeadStatusIndicator from '../components/LeadStatusIndicator';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Lead } from '../types/lead';
import { useCreateNotification } from '../hooks/useCreateNotification';
import { NOTIFICATION_TYPES } from '../utils/notificationUtils';

const statusColors: Record<string, "info" | "warning" | "primary" | "success" | "error"> = {
  "Novo": 'info',
  "Contatado": 'warning',
  "Qualificado": 'primary',
  "Convertido": 'success',
  "Perdido": 'error',
};

interface LeadFormData {
  name: string;
  email: string;
  phone: string;
  source: string;
  status: string;
  notes: string;
  productInterest: string;
}

const LeadsPage: React.FC = () => {
  const dispatch = useDispatch();
  const createNotification = useCreateNotification();
  const leads = useSelector((state: RootState) => state.leads.items);
  const products = useSelector((state: RootState) => state.products.items);
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [formData, setFormData] = useState<LeadFormData>({
    name: '',
    email: '',
    phone: '',
    source: 'Site',
    status: 'Novo',
    notes: '',
    productInterest: '',
  });

  useEffect(() => {
    const loadLeads = async () => {
      try {
        setLoading(true);
        
        // Carregar leads do MongoDB via API
        let apiLeads: Lead[] = [];
        try {
          const apiResponse = await api.getLeads();
          apiLeads = apiResponse.data;
        } catch (apiError) {
          console.error('Erro ao carregar leads da API:', apiError);
        }
        
        // Carregar leads do localStorage
        const localLeads = await mockDataService.getLeads();
        
        // Mesclar leads, evitando duplicatas por ID ou número de telefone
        const mergedLeads: Lead[] = [...apiLeads];
        
        // Adicionar leads do localStorage que não existem na API
        localLeads.forEach((localLead: Lead) => {
          // Verificar se o lead já existe na lista mesclada
          const exists = mergedLeads.some(l => 
            l.id === localLead.id || 
            (l.phone === localLead.phone && localLead.phone) ||
            (l.email === localLead.email && localLead.email && l.email)
          );
          
          if (!exists) {
            mergedLeads.push(localLead);
          }
        });
        
        dispatch(setLeads(mergedLeads));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao carregar leads:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLeads();
  }, [dispatch]);

  const handleStatusChange = (leadId: string) => (event: SelectChangeEvent) => {
    try {
      const newStatus = event.target.value;
      
      // Atualiza o status no Redux
      dispatch(updateLeadStatus({
        id: leadId,
        status: newStatus,
      }));
      
      // Atualiza o lead no localStorage
      const lead = leads.find(l => l.id === leadId);
      if (lead) {
        const updatedLead = {
          ...lead,
          status: newStatus,
          lastContactDate: new Date().toISOString()
        };
        mockDataService.updateLead(leadId, updatedLead);
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar o status. Por favor, tente novamente.');
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch (error) {
      return '-';
    }
  };

  const handleCloseAddDialog = () => setOpenAddDialog(false);
  const handleCloseImportDialog = () => setOpenImportDialog(false);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSourceChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      source: event.target.value,
    });
  };

  const handleStatusFormChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      status: event.target.value,
    });
  };

  const handleProductInterestChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      productInterest: event.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const newLead = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        source: formData.source,
        status: formData.status,
        notes: formData.notes,
        lastContactDate: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        productInterest: formData.productInterest || undefined
      };
      
      // Cria o lead usando o serviço que já implementa a geração do código único
      const createdLead = await mockDataService.createLead(newLead);
      dispatch(addLead(createdLead));

      // Criar notificação
      createNotification({
        ...NOTIFICATION_TYPES.LEAD.CREATED,
        message: `Novo lead criado: ${createdLead.name}`,
        link: `/leads/${createdLead.id}`,
        linkText: 'Ver lead'
      });
      
      handleCloseAddDialog();
      setFormData({
        name: '',
        email: '',
        phone: '',
        source: 'Site',
        status: 'Novo',
        notes: '',
        productInterest: '',
      });
    } catch (error) {
      console.error('Erro ao criar lead:', error);
      createNotification({
        ...NOTIFICATION_TYPES.LEAD.ERROR,
        message: 'Erro ao criar lead. Por favor, tente novamente.'
      });
    }
  };

  const handleDeleteLead = async (id: string) => {
    try {
      const leadToDelete = leads.find(l => l.id === id);
      await mockDataService.deleteLead(id);
      dispatch(deleteLead(id));

      if (leadToDelete) {
        createNotification({
          ...NOTIFICATION_TYPES.LEAD.DELETED,
          message: `Lead excluído: ${leadToDelete.name}`,
          link: '/leads',
          linkText: 'Ver leads'
        });
      }
    } catch (error) {
      console.error('Erro ao excluir lead:', error);
      createNotification({
        ...NOTIFICATION_TYPES.LEAD.ERROR,
        message: 'Erro ao excluir lead. Por favor, tente novamente.'
      });
    }
  };

  const getProductLink = (productId: string) => {
    if (productId === "automatic_later") {
      return <span style={{ fontStyle: 'italic' }}>Automático, vamos falar mais tarde</span>;
    }
    
    const product = products.find(p => p.id === productId);
    if (product) {
      return (
        <Link to={`/produtos/${productId}`} style={{ textDecoration: 'none', color: '#1976d2' }}>
          {product.name}
        </Link>
      );
    }
    return '-';
  };

  // Função para exportar leads para Excel
  const exportLeadsToExcel = () => {
    // Preparar os dados para exportação
    const exportData = leads.map(lead => {
      let productName = '';
      if (lead.productInterest === 'automatic_later') {
        productName = 'Automático, vamos falar mais tarde';
      } else if (lead.productInterest) {
        const product = products.find(p => p.id === lead.productInterest);
        productName = product ? product.name : '';
      }
      
      return {
        Nome: lead.name,
        Email: lead.email,
        Telefone: lead.phone || '',
        Origem: lead.source,
        Status: lead.status,
        'Produto de Interesse': productName,
        'Última Interação': lead.lastContactDate ? new Date(lead.lastContactDate).toLocaleDateString('pt-BR') : '',
        Observações: lead.notes || ''
      };
    });

    // Criar uma planilha
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Ajustar largura das colunas
    const columnWidths = [
      { wch: 20 }, // Nome
      { wch: 25 }, // Email
      { wch: 15 }, // Telefone
      { wch: 12 }, // Origem
      { wch: 12 }, // Status
      { wch: 25 }, // Produto de Interesse
      { wch: 15 }, // Última Interação
      { wch: 40 }  // Observações
    ];
    worksheet['!cols'] = columnWidths;
    
    // Criar um workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads');
    
    // Gerar o arquivo
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Nome do arquivo com data atual
    const fileName = `leads_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, fileName);
  };

  // Função para converter status do lead para o tipo da sinaleira
  const convertLeadStatus = (status: string): 'active' | 'warning' | 'critical' => {
    const daysSinceLastContact = (lastContactDate: string) => {
      const lastContact = new Date(lastContactDate);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - lastContact.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    switch (status) {
      case 'Novo':
      case 'Qualificado':
        return 'active';
      case 'Contatado':
        return 'warning';
      case 'Perdido':
        return 'critical';
      default:
        return 'active';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gerenciamento de Leads
        </Typography>
        <ButtonGroup variant="contained">
          <Button 
            startIcon={<AddIcon />} 
            onClick={() => setOpenAddDialog(true)}
          >
            Adicionar Lead
          </Button>
          <Button 
            startIcon={<FileUploadIcon />} 
            onClick={() => setOpenImportDialog(true)}
          >
            Importar
          </Button>
          <Button 
            startIcon={<FileDownloadIcon />} 
            onClick={exportLeadsToExcel}
            color="secondary"
          >
            Exportar
          </Button>
        </ButtonGroup>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>Origem</TableCell>
                <TableCell>Produto de Interesse</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.map((lead) => {
                const productOfInterest = lead.productInterest && lead.productInterest !== 'automatic_later'
                  ? products.find(p => p.id === lead.productInterest) 
                  : null;
                
                return (
                  <TableRow key={lead.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LeadTooltip lead={lead} product={productOfInterest}>
                          <Box>
                            <Link 
                              to={`/leads/${lead.id}`}
                              style={{ 
                                textDecoration: 'none', 
                                color: 'inherit',
                                display: 'block',
                                fontWeight: 'bold'
                              }}
                            >
                              {lead.name}
                            </Link>
                            <Typography variant="caption" color="textSecondary">
                              {lead.uniqueCode}
                            </Typography>
                          </Box>
                        </LeadTooltip>
                        <LeadStatusIndicator status={convertLeadStatus(lead.status)} />
                      </Box>
                    </TableCell>
                    <TableCell>{lead.email}</TableCell>
                    <TableCell>{lead.phone || '-'}</TableCell>
                    <TableCell>{lead.source}</TableCell>
                    <TableCell>
                      {lead.productInterest ? getProductLink(lead.productInterest) : '-'}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={lead.status}
                        onChange={handleStatusChange(lead.id)}
                        size="small"
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value="Novo">
                          <Chip
                            label="Novo"
                            size="small"
                            color={statusColors["Novo"]}
                          />
                        </MenuItem>
                        <MenuItem value="Contatado">
                          <Chip
                            label="Contatado"
                            size="small"
                            color={statusColors["Contatado"]}
                          />
                        </MenuItem>
                        <MenuItem value="Qualificado">
                          <Chip
                            label="Qualificado"
                            size="small"
                            color={statusColors["Qualificado"]}
                          />
                        </MenuItem>
                        <MenuItem value="Convertido">
                          <Chip
                            label="Convertido"
                            size="small"
                            color={statusColors["Convertido"]}
                          />
                        </MenuItem>
                        <MenuItem value="Perdido">
                          <Chip
                            label="Perdido"
                            size="small"
                            color={statusColors["Perdido"]}
                          />
                        </MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {formatDate(lead.lastContactDate)}
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        component={Link} 
                        to={`/leads/${lead.id}`}
                        color="primary"
                        size="small"
                        aria-label="ver perfil"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal para adicionar lead manualmente */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle>Adicionar Novo Lead</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  autoFocus
                  margin="dense"
                  name="name"
                  label="Nome Completo"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleTextChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  name="email"
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={handleTextChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  name="phone"
                  label="Telefone"
                  fullWidth
                  value={formData.phone}
                  onChange={handleTextChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="source-label">Origem</InputLabel>
                  <Select
                    labelId="source-label"
                    name="source"
                    value={formData.source}
                    onChange={handleSourceChange}
                    label="Origem"
                  >
                    <MenuItem value="Site">Site</MenuItem>
                    <MenuItem value="Facebook">Facebook</MenuItem>
                    <MenuItem value="Instagram">Instagram</MenuItem>
                    <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                    <MenuItem value="Twitter">Twitter</MenuItem>
                    <MenuItem value="WhatsApp">WhatsApp</MenuItem>
                    <MenuItem value="Indicação">Indicação</MenuItem>
                    <MenuItem value="Outro">Outro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    name="status"
                    value={formData.status}
                    onChange={handleStatusFormChange}
                    label="Status"
                  >
                    <MenuItem value="Novo">Novo</MenuItem>
                    <MenuItem value="Contatado">Contatado</MenuItem>
                    <MenuItem value="Qualificado">Qualificado</MenuItem>
                    <MenuItem value="Convertido">Convertido</MenuItem>
                    <MenuItem value="Perdido">Perdido</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="product-interest-label">Produto de Interesse</InputLabel>
                  <Select
                    labelId="product-interest-label"
                    name="productInterest"
                    value={formData.productInterest}
                    onChange={handleProductInterestChange}
                    label="Produto de Interesse"
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    <MenuItem value="automatic_later">
                      <em>Automático, vamos falar mais tarde</em>
                    </MenuItem>
                    {products.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.name} - R$ {product.price.toFixed(2)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Selecione o produto ou serviço de interesse</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  name="notes"
                  label="Observações"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={handleTextChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddDialog}>Cancelar</Button>
            <Button type="submit" variant="contained" color="primary">
              Adicionar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Componente de importação de leads */}
      <LeadImporter 
        open={openImportDialog} 
        onClose={handleCloseImportDialog} 
        products={products}
      />
    </Container>
  );
};

export default LeadsPage; 